<template>
  <div v-loading="loading" class="services-tab">
    <div class="row mb-2" v-if="validationErrors">
      <div class="col">
        <el-alert type="error">
          {{ validationErrors }}
        </el-alert>
      </div>
    </div>

    <!-- Food & Beverage Requirements Section -->
    <div class="row">
      <div class="col-12">
        <base-input 
          label="Food & Beverage Requirements"
        >
          <div class="mb-2 text-muted small">Please tick all that apply</div>
          <div class="row">
            <div class="col-md-6">
              <el-checkbox-group v-if="foodBeverageOptions" v-model="form.f_and_b_requirements">
                <div v-for="option in foodBeverageOptions.slice(0, Math.ceil(foodBeverageOptions.length / 2))" 
                     :key="option.value"
                     class="mb-2">
                  <el-checkbox 
                    :label="option.value"
                    :key="option.value"
                  >
                    {{ option.label }}
                  </el-checkbox>
                </div>
              </el-checkbox-group>
            </div>
            <div class="col-md-6">
              <el-checkbox-group v-if="foodBeverageOptions" v-model="form.f_and_b_requirements">
                <div v-for="option in foodBeverageOptions.slice(Math.ceil(foodBeverageOptions.length / 2))" 
                     :key="option.value"
                     class="mb-2">
                  <el-checkbox 
                    :label="option.value"
                    :key="option.value"
                  >
                    {{ option.label }}
                  </el-checkbox>
                </div>
              </el-checkbox-group>
            </div>
          </div>
        </base-input>
      </div>
    </div>

    <!-- Suppliers Section -->
    <div class="row mt-4">
      <div class="col-12">
        <base-input 
          label="What suppliers do you require?"
        >
          <div class="mb-2 text-muted small">Please tick all that apply</div>
          <div class="row">
            <div class="col-md-6">
              <el-checkbox-group v-model="form.supplier_preference">
                <div v-for="option in supplierOptions.slice(0, Math.ceil(supplierOptions.length / 2))" 
                     :key="option.value"
                     class="mb-2">
                  <el-checkbox 
                    :label="option.value"
                    :key="option.value"
                  >
                    {{ option.label }}
                  </el-checkbox>
                </div>
              </el-checkbox-group>
            </div>
            <div class="col-md-6">
              <el-checkbox-group v-model="form.supplier_preference">
                <div v-for="option in supplierOptions.slice(Math.ceil(supplierOptions.length / 2))" 
                     :key="option.value"
                     class="mb-2">
                  <el-checkbox 
                    :label="option.value"
                    :key="option.value"
                  >
                    {{ option.label }}
                  </el-checkbox>
                </div>
              </el-checkbox-group>
            </div>
          </div>
        </base-input>
      </div>
    </div>

    <!-- Accommodation Section -->
    <div class="row mt-4">
      <div class="col-12">
            <base-input 
            label="Do you require accommodation onsite?"
            >
            <el-radio-group v-model="form.accommodation_onsite_required">
                <el-radio :label="false">No</el-radio>
                <el-radio :label="true">Yes</el-radio>
            </el-radio-group>
            </base-input>
        </div>
    </div>
    <div class="row">
        <div class="col-6">
            <div v-if="form.accommodation_onsite_required" class="mt-3">
                <base-input 
                    label="How many rooms are required?"
                    type="number"
                    v-model="form.no_of_rooms_required"
                    placeholder="Enter number of rooms"
                />
            </div>
        </div>
        <div class="col-6">
            <div v-if="form.accommodation_onsite_required" class="mt-3">
                <base-input 
                    label="Who will accommodation be billed to?"
                    class="mt-3"
                >
                    <el-select 
                        v-model="form.accommodation_billed_to" 
                        placeholder="Select billing option"
                        :clearable="true"
                    >
                        <el-option
                            v-for="option in accommodationBillingOptions"
                            :key="option.value"
                            :label="option.label"
                            :value="option.value"
                        />
                    </el-select>
                </base-input>
            </div>
        </div>
    </div>

    <!-- Save Button -->
    <div class="row mt-4">
      <div class="col-12 text-right">
        <base-button 
          type="primary" 
          @click="saveServices"
          :loading="loading"
        >
          Save & Next
        </base-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Select,
  Option,
  Notification,
  Checkbox,
  CheckboxGroup,
  Radio,
  RadioGroup
} from "element-ui";
import { mapState } from "vuex";
import LeadEventService from '@/services/lead-event.service';
import leadEventFormMixin from '@/helpers/leadEventFormMixin';

export default {
  name: 'Services',
  mixins: [leadEventFormMixin],
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Notification.name]: Notification,
    [Checkbox.name]: Checkbox,
    [CheckboxGroup.name]: CheckboxGroup,
    [Radio.name]: Radio,
    [RadioGroup.name]: RadioGroup,
  },
  props: {
    leadEvent: {
      type: Object,
      required: true,
    },
    sessionId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      validationErrors: '',
      fieldErrors: {},
      form: {
        step: 3,
        direct_update: true,
        f_and_b_requirements: [],
        supplier_preference: [],
        accommodation_onsite_required: null,
        no_of_rooms_required: null,
        accommodation_billed_to: null,
      },
      foodBeverageOptions: [],
      supplierOptions: [
        { label: 'Band/Live Music', value: 'band_music' },
        { label: 'DJ', value: 'dj' },
        { label: 'Entertainment', value: 'entertainment' },
        { label: 'Photo booth', value: 'photo_booth' },
        { label: 'Videography', value: 'vidoegraphy' },
        { label: 'Content Creation', value: 'content_creation' },
        { label: 'MC', value: 'mc' },
        { label: 'Photographer', value: 'photographer' },
      ],
      accommodationBillingOptions: [
        { label: 'Charged to master account', value: 'master_account' },
        { label: 'Pay own account', value: 'pay_own_account' },
      ],
    };
  },
  computed: {
    ...mapState("country/", {
      country: (state) => state.country || 'AU',
    }),
  },
  methods: {
    async saveServices() {
      this.loading = true;
      try {
        // Send form data directly - arrays are now supported by backend
        if (this.sessionId) {
          await LeadEventService.updateEventSession(this.leadEvent.id, {
            ...this.form,
            session_id: this.sessionId,
            step: 3
          });
        } else {
          await LeadEventService.updateCorporateLeadEvent(this.leadEvent.id, this.form);
        }

        // Emit success event to parent
        this.$emit('onSave', {
          message: 'Services saved successfully',
        });
        
        // Show success message
        Notification.success({
          title: 'Success',
          message: 'Services saved successfully!',
          type: 'success',
        });
        
      } catch (error) {
        console.error('Error saving services:', error);
        // Show error message
        Notification.error({
          title: 'Error',
          message: 'Failed to save services. Please check your inputs and try again.',
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    
    updateFormFromLeadEvent(leadEvent) {

      // Setting default options from leadEvent options.
      this.foodBeverageOptions = leadEvent?.options?.food_and_beverage_requirement || [];

      // Update form fields from leadEvent data, preserving existing values when new data is missing
      const safeUpdate = (currentValue, newValue) => {
        return (newValue !== null && newValue !== undefined && newValue !== '') ? newValue : currentValue;
      };

      let dataToUse = leadEvent;
      if (this.sessionId) {
        const sessions = leadEvent.event_sessions || leadEvent.eventSessions || [];
        const session = sessions.find(s => s.id === this.sessionId);
        if (session) {
          dataToUse = {
            ...leadEvent, // Fallback to leadEvent for missing fields
            ...session
          };
        }
      }

      // Handle array fields (backend now sends arrays directly)
      const safeArrayUpdate = (currentValue, newValue) => {
        if (Array.isArray(newValue)) {
          return newValue;
        }
        if (typeof newValue === 'string' && newValue.trim()) {
          try {
            // Try to parse as JSON if it's a string
            return JSON.parse(newValue);
          } catch {
            // If not JSON, split by comma (fallback for string format)
            return newValue.split(',').map(item => item.trim()).filter(item => item);
          }
        }
        return currentValue || [];
      };

      // Handle accommodation onsite boolean conversion
      let accommodationOnsite = this.safeBooleanUpdate(dataToUse.accommodation_onsite);

      this.form.f_and_b_requirements = safeArrayUpdate(this.form.f_and_b_requirements, dataToUse.f_and_b_requirements);
      this.form.supplier_preference = safeArrayUpdate(this.form.supplier_preference, dataToUse.supplier_preference);
      this.form.accommodation_onsite_required = safeUpdate(this.form.accommodation_onsite_required, accommodationOnsite);
      this.form.no_of_rooms_required = safeUpdate(this.form.no_of_rooms_required, dataToUse.no_of_rooms);
      this.form.accommodation_billed_to = safeUpdate(this.form.accommodation_billed_to, dataToUse.accommodation_billed_to);

      console.log('Services form updated with data:', this.form);
    }
  },
  mounted() {
    // Initialize form with leadEvent data when component is mounted
    if (this.leadEvent) {
      this.updateFormFromLeadEvent(this.leadEvent);
    }
  },
  watch: {
    leadEvent: {
      handler(newLeadEvent) {
        if (newLeadEvent) {
          this.updateFormFromLeadEvent(newLeadEvent);
        }
      },
      deep: true,
      immediate: true
    },
    sessionId: {
      handler(newSessionId) {
        this.updateFormFromLeadEvent(this.leadEvent);
      },
      immediate: false
    }
  }
};
</script>

<style lang="scss" scoped>
.services-tab {
  // Any custom styles if needed
  .el-checkbox {
    display: block;
    margin-bottom: 8px;
  }
  
  .el-radio {
    margin-right: 20px;
  }
}
</style>
